import { defineComponent as _defineComponent } from 'vue'
import ConfirmDeleteModal from "@/components/ui/modals/ConfirmDeleteModal.vue";
  import CheckboxInput from "@/components/ui/input/CheckboxInput.vue";
  import InputSearch from "@/components/ui/input/InputSearch.vue";
  import { computed, onMounted, ref, watch } from "vue";
  import { LotInterface } from "@/types/LotInterface";
  import store from "@/store";
  import router from "@/router";
  import { getLotStatusClass } from "@/helpers/status";
  import { isValidFileSize, isXlsxType } from "@/helpers/fileUpload";
  import { formatPrice } from "@/helpers/transformation/price";
  import plural from "@/features/plural";
  import { debounce } from "@/helpers/debounce";
  import { useToast } from "vue-toastification";
  import { UserInterface } from "@/types/UserInterface";
  import { canEditLot } from "@/helpers/lot/adminLot";
  import { LotStatus } from "@/enums/LotStatus";

  
export default /*@__PURE__*/_defineComponent({
  __name: 'AdminStageLots',
  props: {
    estateId: { type: String, required: false },
    landownerId: { type: String, required: false },
    stageId: { type: String, required: false },
    isConciergeView: { type: Boolean, default: false },
  },
  emits: ["lotSelected"],
  setup(__props, { expose: __expose, emit: __emit }) {
  __expose();

  const props = __props;
  const emit = __emit;
  const toast = useToast();
  const userId = computed(
    () => props.landownerId || router.currentRoute.value.params.userId
  );
  const estateId = computed(
    () => props.estateId || router.currentRoute.value.params.estateId
  );
  const stageId = computed(
    () => props.stageId || router.currentRoute.value.params.stageId
  );
  const lots = ref<LotInterface[]>([]);
  const searchQuery = ref("");
  const debounceSearch = debounce(getLots);
  const showConfirmDeleteModal = ref(false);
  const showConfirmBulkDeleteModal = ref(false);
  const itemForDelete = ref({ id: "", title: "" });
  const templateDownloadLink = ref<string | null>(null);
  const lotList = ref([]);
  const isSelectAll = ref(false);
  const selectedLotsList = computed(() => {
    return lotList.value.filter((item) => item.selected);
  });
  const user = ref<UserInterface | null>(null);
  const isUserActive = computed(() => {
    return user.value?.verifiedEmail;
  });
  const selectedIndex = ref<number>(-1);

  watch(
    estateId,
    (newEstateId) => {
      if (newEstateId) {
        getUser();
        getLots();
        getTemplateLink();
      }
    },
    { immediate: true }
  );

  watch(
    () => searchQuery.value,
    () => {
      debounceSearch();
    }
  );

  onMounted(() => {
    getUser();
    getLots();
    getTemplateLink();
  });

  function getUser() {
    store.dispatch("user/getUser", userId.value).then((res) => {
      user.value = res.data;
    });
  }
  function getLots() {
    store
      .dispatch("lot/getLots", {
        title: searchQuery.value,
        stageId: stageId.value,
      })
      .then((res) => {
        lots.value = res.data;
        lotList.value = lots.value.map((item) => {
          return {
            id: item._id,
            selected: false,
            status: item.status,
          };
        });
        isSelectAll.value = false;
      });
  }
  function selectLot(evnt: boolean, index: number) {
    lotList.value[index].selected = !lotList.value[index].selected;
    isSelectAll.value = selectedLotsList.value.length > 0;
  }

  function selectAll(isSelect: boolean) {
    isSelectAll.value = isSelect;
    lotList.value.forEach((item) => {
      item.selected = isSelect;
    });
  }
  function bulkUpload(e: any) {
    if (!e.target.files.length) {
      return;
    }

    const selectedFile = e.target.files[0];

    if (!isXlsxType(selectedFile) || !isValidFileSize(selectedFile)) {
      return;
    }

    const data = new FormData();

    data.append("file", selectedFile);
    store
      .dispatch("lot/bulkLotUpload", { stageId: stageId.value, data })
      .then(() => getLots())
      .catch(() => toast.error("Invalid file data"));
  }
  function getTemplateLink() {
    store.dispatch("lot/downloadXlsxTemplate").then((res) => {
      templateDownloadLink.value = res.data.message ? null : res.data;
    });
  }
  function toLot(id: string) {
    router.push({
      name: "AdminLot",
      params: {
        userId: userId.value,
        estateId: estateId.value,
        stageId: stageId.value,
        lotId: id,
      },
    });
  }
  function toCreateLot() {
    router.push({
      name: "AdminLotCreate",
      params: {
        userId: userId.value,
        estateId: estateId.value,
        stageId: stageId.value,
      },
    });
  }
  const deleteLot = (id: string) => {
    store
      .dispatch("lot/deleteLots", {
        lotIds: [id],
      })
      .then(() => {
        getLots();
        toast.success("The lot was successfully deleted");
      })
      .catch((err) => {
        if (err.response.data?.message) {
          toast.error(err.response.data.message);
        } else {
          toast.error("Something went wrong");
        }
      });
  };
  const bulkDeleteLots = () => {
    store
      .dispatch("lot/deleteLots", {
        lotIds: selectedLotsList.value.map((item) => item.id),
      })
      .then(() => {
        getLots();
        toast.success("The lots was successfully deleted");
      })
      .catch((err) => {
        if (err.response.data?.message) {
          toast.error(err.response.data.message);
        } else {
          toast.error("Something went wrong");
        }
      });
  };

  function setItemForDelete(lot: LotInterface) {
    itemForDelete.value = { id: lot._id, title: lot.title };
    showConfirmDeleteModal.value = true;
  }
  const isClickable = (lot: LotInterface, index: number) => {
    return lot.status === LotStatus.AVAILABLE && lot.builders.length > 0;
  };

  const toLotSelectedIfClickeable = (lot: LotInterface, index: number) => {
    if (isClickable(lot, index)) {
      toLotSelected(lot._id, index, lot.title, lot.size, lot.price);
    }
  };

  const handleRowClick = (lot: LotInterface, index: number) => {
    if (props.isConciergeView) {
      toLotSelectedIfClickeable(lot, index);
    } else {
      toLot(lot._id);
    }
  };
  const toLotSelected = (
    id: string,
    index: number,
    lotTitle: string,
    lotSize: number,
    lotPrice: number
  ) => {
    emit("lotSelected", id, lotTitle, lotSize, lotPrice);

    lotList.value.forEach((lot) => {
      lot.selected = false;
    });

    lotList.value[index].selected = true;

    isSelectAll.value = selectedLotsList.value.length > 0;

    selectedIndex.value = index;
  };
  const getButtonLabel = (lot: LotInterface, index: number) => {
    if (!isClickable(lot, index)) {
      return "Select";
    }

    return selectedIndex.value !== -1 && index !== selectedIndex.value
      ? "Change"
      : "Select";
  };

const __returned__ = { props, emit, toast, userId, estateId, stageId, lots, searchQuery, debounceSearch, showConfirmDeleteModal, showConfirmBulkDeleteModal, itemForDelete, templateDownloadLink, lotList, isSelectAll, selectedLotsList, user, isUserActive, selectedIndex, getUser, getLots, selectLot, selectAll, bulkUpload, getTemplateLink, toLot, toCreateLot, deleteLot, bulkDeleteLots, setItemForDelete, isClickable, toLotSelectedIfClickeable, handleRowClick, toLotSelected, getButtonLabel, ConfirmDeleteModal, CheckboxInput, InputSearch, computed, onMounted, ref, watch, get LotInterface() { return LotInterface }, get store() { return store }, get router() { return router }, get getLotStatusClass() { return getLotStatusClass }, get isValidFileSize() { return isValidFileSize }, get isXlsxType() { return isXlsxType }, get formatPrice() { return formatPrice }, get plural() { return plural }, get debounce() { return debounce }, get useToast() { return useToast }, get UserInterface() { return UserInterface }, get canEditLot() { return canEditLot }, get LotStatus() { return LotStatus } }
Object.defineProperty(__returned__, '__isScriptSetup', { enumerable: false, value: true })
return __returned__
}

})